import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Crown, Check, Building2, Users, Calendar, AlertTriangle, 
  CreditCard, Zap, Clock, ArrowRight, X, RefreshCw, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '@/components/shared/AppHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

export default function MyPlanPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [processing, setProcessing] = useState(false);
  
  // Netflix-style state
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // Toggle: monthly/yearly
  const [selectedTier, setSelectedTier] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {type, tier, cycle, price}

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [subRes, plansRes] = await Promise.all([
        fetch(`${API_URL}/api/tenant/my-subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/subscription/plans`)
      ]);
      
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
        // Set current billing cycle
        if (subData.billing_cycle) {
          setBillingCycle(subData.billing_cycle === 'annual' ? 'yearly' : 'monthly');
        }
      }
      
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData.plans || []);
      }
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  // Get current plan info
  const getCurrentTier = () => {
    if (!subscription?.plan) return 'starter';
    return subscription.plan.replace('_monthly', '').replace('_annual', '').replace('_yearly', '');
  };

  const getCurrentCycle = () => {
    if (!subscription?.billing_cycle) return 'monthly';
    return subscription.billing_cycle === 'annual' ? 'yearly' : subscription.billing_cycle;
  };

  // Plan hierarchy
  const TIERS = ['starter', 'professional', 'enterprise'];
  const getTierLevel = (tier) => TIERS.indexOf(tier);

  // Get plan data for a tier
  const getPlanData = (tier) => {
    return plans.find(p => p.tier === tier) || null;
  };

  // Get price for tier and cycle
  const getPrice = (tier, cycle) => {
    const plan = getPlanData(tier);
    if (!plan) return 0;
    return plan[cycle]?.price || 0;
  };

  // Determine action type
  const getActionType = (newTier, newCycle) => {
    const currentTier = getCurrentTier();
    const currentCycle = getCurrentCycle();
    const isTrial = subscription?.is_trial;
    const isExpired = subscription?.is_expired;

    if (isTrial || isExpired) return 'subscribe';
    
    const currentLevel = getTierLevel(currentTier);
    const newLevel = getTierLevel(newTier);

    if (newTier === currentTier && newCycle === currentCycle) return 'renew';
    if (newLevel > currentLevel) return 'upgrade';
    if (newLevel < currentLevel) return 'downgrade';
    
    // Same tier, different cycle
    if (newCycle === 'yearly') return 'upgrade'; // Monthly -> Yearly is upgrade
    return 'downgrade'; // Yearly -> Monthly is downgrade
  };

  // Handle plan selection
  const handleSelectPlan = (tier) => {
    const actionType = getActionType(tier, billingCycle);
    const price = getPrice(tier, billingCycle);
    const plan = getPlanData(tier);
    
    // Check usage limits for downgrade
    if (actionType === 'downgrade') {
      const limits = {
        locations: plan?.locations_limit || 3,
        therapists: plan?.therapists_limit || 10
      };
      const usage = subscription?.usage || { locations: 0, therapists: 0 };
      
      if (limits.locations !== -1 && usage.locations > limits.locations) {
        toast.error(`Archive ${usage.locations - limits.locations} location(s) first to downgrade`);
        return;
      }
      if (limits.therapists !== -1 && usage.therapists > limits.therapists) {
        toast.error(`Archive ${usage.therapists - limits.therapists} therapist(s) first to downgrade`);
        return;
      }
    }

    setConfirmAction({
      type: actionType,
      tier,
      cycle: billingCycle,
      price,
      plan
    });
  };

  // Process the confirmed action
  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    
    setProcessing(true);
    const token = localStorage.getItem('token');

    try {
      if (confirmAction.type === 'downgrade') {
        // Downgrade = Schedule (no payment)
        const res = await fetch(`${API_URL}/api/subscription/change-plan`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            new_plan_tier: confirmAction.tier,
            new_billing_cycle: confirmAction.cycle
          })
        });

        const data = await res.json();
        if (res.ok) {
          toast.success(data.message || 'Downgrade scheduled!');
          setConfirmAction(null);
          setShowChangePlan(false);
          fetchData();
        } else {
          toast.error(data.detail || 'Failed to schedule downgrade');
        }
      } else {
        // Upgrade/Renew/Subscribe = Payment required
        const res = await fetch(`${API_URL}/api/subscription/change-plan`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            new_plan_tier: confirmAction.tier,
            new_billing_cycle: confirmAction.cycle
          })
        });

        const data = await res.json();
        
        if (!res.ok) {
          toast.error(data.detail || 'Failed to process');
          return;
        }

        if (data.requires_payment) {
          await processPayment(data, confirmAction);
        } else {
          toast.success(data.message || 'Plan updated!');
          setConfirmAction(null);
          setShowChangePlan(false);
          fetchData();
        }
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  // Process Razorpay payment
  const processPayment = async (orderData, action) => {
    // Load Razorpay
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      document.body.appendChild(script);
      await new Promise(resolve => script.onload = resolve);
    }

    const options = {
      key: orderData.razorpay_key,
      amount: orderData.amount * 100,
      currency: 'INR',
      name: 'WellNest Technologies',
      description: `${action.tier.charAt(0).toUpperCase() + action.tier.slice(1)} Plan`,
      order_id: orderData.order_id,
      handler: async (response) => {
        // Verify payment
        try {
          const token = localStorage.getItem('token');
          const verifyRes = await fetch(`${API_URL}/api/subscription/verify-payment`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              action_type: action.type,
              new_plan_tier: action.tier,
              new_billing_cycle: action.cycle
            })
          });

          if (verifyRes.ok) {
            const result = await verifyRes.json();
            toast.success(result.message || 'Payment successful!');
            setConfirmAction(null);
            setShowChangePlan(false);
            fetchData();
          } else {
            toast.error('Payment verification failed');
          }
        } catch (err) {
          toast.error('Payment verification error');
        }
      },
      prefill: {
        email: subscription?.admin_email || user?.email,
        contact: subscription?.admin_phone || ''
      },
      theme: { color: '#0d9488' }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // Cancel scheduled downgrade
  const handleCancelScheduled = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/subscription/cancel-scheduled-change`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Scheduled change cancelled');
        fetchData();
      } else {
        toast.error('Failed to cancel');
      }
    } catch (error) {
      toast.error('Error cancelling');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  const currentTier = getCurrentTier();
  const currentCycle = getCurrentCycle();
  const isTrial = subscription?.is_trial;
  const isExpired = subscription?.is_expired;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <AppHeader user={user} onLogout={onLogout} showBack backTo="/admin" title="My Plan" />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        
        {/* ========== CURRENT PLAN CARD ========== */}
        <Card className={`mb-8 overflow-hidden ${
          isExpired ? 'border-red-500/50 bg-red-500/5' :
          isTrial ? 'border-amber-500/50 bg-amber-500/5' :
          'border-teal-500/50 bg-teal-500/5'
        }`} data-testid="current-plan-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  isExpired ? 'bg-red-500/20' : isTrial ? 'bg-amber-500/20' : 'bg-teal-500/20'
                }`}>
                  {isExpired ? <AlertTriangle className="w-7 h-7 text-red-400" /> :
                   isTrial ? <Clock className="w-7 h-7 text-amber-400" /> :
                   <Crown className="w-7 h-7 text-teal-400" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-white capitalize" data-testid="plan-name">
                      {isExpired ? 'Expired' : isTrial ? 'Free Trial' : currentTier}
                    </h2>
                    {!isTrial && !isExpired && (
                      <Badge variant="outline" className="text-slate-400 border-slate-600">
                        {currentCycle === 'yearly' ? 'Annual' : 'Monthly'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-400 mt-1" data-testid="plan-status">
                    {isExpired ? 'Your subscription has expired' :
                     isTrial ? `Trial ends in ${subscription?.days_remaining || 0} days` :
                     subscription?.auto_renewal ? 
                       `Renews ${new Date(subscription?.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` :
                       `Expires ${new Date(subscription?.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                  </p>
                </div>
              </div>
              
              {/* Usage Stats */}
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="flex items-center gap-1 text-white">
                    <Building2 className="w-4 h-4 text-blue-400" />
                    <span className="text-xl font-bold">{subscription?.usage?.locations || 0}</span>
                    <span className="text-slate-500">/ {subscription?.limits?.locations === -1 ? '∞' : subscription?.limits?.locations}</span>
                  </div>
                  <p className="text-xs text-slate-500">Locations</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-1 text-white">
                    <Users className="w-4 h-4 text-purple-400" />
                    <span className="text-xl font-bold">{subscription?.usage?.therapists || 0}</span>
                    <span className="text-slate-500">/ {subscription?.limits?.therapists === -1 ? '∞' : subscription?.limits?.therapists}</span>
                  </div>
                  <p className="text-xs text-slate-500">Staff</p>
                </div>
              </div>
            </div>

            {/* Scheduled Change Banner */}
            {subscription?.scheduled_plan_change && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm">
                    Changing to <strong className="capitalize">{subscription.scheduled_plan_change.new_tier}</strong> on {new Date(subscription.scheduled_plan_change.scheduled_for).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <Button size="sm" variant="ghost" onClick={handleCancelScheduled} className="text-red-400 hover:text-red-300">
                  Cancel
                </Button>
              </div>
            )}

            {/* Action Button */}
            <div className="mt-6">
              <Button 
                onClick={() => setShowChangePlan(true)}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                data-testid="change-plan-btn"
              >
                {isExpired || isTrial ? 'Choose a Plan' : 'Change Plan'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ========== PLAN SELECTION (Netflix Style) ========== */}
        {showChangePlan && (
          <Card className="bg-slate-900/80 border-slate-700" data-testid="plan-selection">
            <CardContent className="p-6">
              {/* Header with Close */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Choose Your Plan</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowChangePlan(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Monthly/Yearly Toggle */}
              <div className="flex items-center justify-center gap-4 mb-8 p-2 bg-slate-800 rounded-full w-fit mx-auto">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    billingCycle === 'monthly' 
                      ? 'bg-teal-500 text-white' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="toggle-monthly"
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    billingCycle === 'yearly' 
                      ? 'bg-teal-500 text-white' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="toggle-yearly"
                >
                  Yearly
                  <Badge className="bg-green-500/20 text-green-400 text-xs">Save 17%</Badge>
                </button>
              </div>

              {/* Plan Cards Grid */}
              <div className="grid md:grid-cols-3 gap-4">
                {plans.map((plan) => {
                  const isCurrentPlan = plan.tier === currentTier && billingCycle === currentCycle && !isTrial && !isExpired;
                  const price = plan[billingCycle]?.price || 0;
                  const originalPrice = plan[billingCycle]?.original_price || price;
                  const actionType = getActionType(plan.tier, billingCycle);
                  
                  // Check if downgrade blocked
                  const isDowngradeBlocked = actionType === 'downgrade' && (
                    (plan.locations_limit !== -1 && (subscription?.usage?.locations || 0) > plan.locations_limit) ||
                    (plan.therapists_limit !== -1 && (subscription?.usage?.therapists || 0) > plan.therapists_limit)
                  );

                  return (
                    <div
                      key={plan.tier}
                      className={`relative p-6 rounded-2xl border-2 transition-all cursor-pointer ${
                        isCurrentPlan 
                          ? 'border-teal-500 bg-teal-500/10' 
                          : isDowngradeBlocked
                          ? 'border-slate-700 bg-slate-800/30 opacity-50 cursor-not-allowed'
                          : 'border-slate-700 bg-slate-800/30 hover:border-slate-500'
                      }`}
                      onClick={() => !isDowngradeBlocked && !isCurrentPlan && handleSelectPlan(plan.tier)}
                      data-testid={`plan-${plan.tier}`}
                    >
                      {/* Current Badge */}
                      {isCurrentPlan && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-500 text-white">
                          Current Plan
                        </Badge>
                      )}
                      
                      {/* Popular Badge for Professional */}
                      {plan.tier === 'professional' && !isCurrentPlan && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white">
                          Most Popular
                        </Badge>
                      )}

                      {/* Plan Name */}
                      <h4 className="text-lg font-bold text-white capitalize mb-2">{plan.tier}</h4>
                      
                      {/* Price */}
                      <div className="mb-4">
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-white">{formatCurrency(price)}</span>
                          <span className="text-slate-400">/{billingCycle === 'yearly' ? 'yr' : 'mo'}</span>
                        </div>
                        {originalPrice > price && (
                          <p className="text-sm text-slate-500 line-through">{formatCurrency(originalPrice)}</p>
                        )}
                      </div>

                      {/* Limits */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-slate-300">
                          <Building2 className="w-4 h-4 text-blue-400" />
                          {plan.locations_limit === -1 ? 'Unlimited' : plan.locations_limit} Locations
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-300">
                          <Users className="w-4 h-4 text-purple-400" />
                          {plan.therapists_limit === -1 ? 'Unlimited' : plan.therapists_limit} Staff
                        </div>
                      </div>

                      {/* Features */}
                      <div className="space-y-1">
                        {plan.features?.slice(0, 3).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-slate-400">
                            <Check className="w-3 h-3 text-teal-400" />
                            {feature}
                          </div>
                        ))}
                      </div>

                      {/* Action indicator */}
                      {!isCurrentPlan && !isDowngradeBlocked && (
                        <div className="mt-4 pt-4 border-t border-slate-700">
                          <p className={`text-xs font-medium ${
                            actionType === 'upgrade' ? 'text-green-400' :
                            actionType === 'downgrade' ? 'text-amber-400' :
                            'text-teal-400'
                          }`}>
                            {actionType === 'upgrade' && '↑ Upgrade'}
                            {actionType === 'downgrade' && '↓ Downgrade'}
                            {actionType === 'renew' && '↻ Renew'}
                            {actionType === 'subscribe' && '→ Subscribe'}
                          </p>
                        </div>
                      )}

                      {isDowngradeBlocked && (
                        <p className="mt-4 text-xs text-red-400">
                          Usage exceeds limits
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Netflix-style info */}
              <div className="mt-8 p-4 bg-slate-800/50 rounded-xl">
                <h4 className="text-white font-medium mb-3">How it works</h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-3 h-3 text-green-400 rotate-[-45deg]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Upgrades</p>
                      <p className="text-slate-400 text-xs">Immediate. Pay full price. Cycle resets from today.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-3 h-3 text-amber-400 rotate-[45deg]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Downgrades</p>
                      <p className="text-slate-400 text-xs">Scheduled. Takes effect at period end. No payment.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-3 h-3 text-teal-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Renewals</p>
                      <p className="text-slate-400 text-xs">Same plan? Days added to expiry. No reset.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ========== CONFIRMATION MODAL ========== */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="bg-slate-900 border-slate-700 max-w-md w-full">
              <CardContent className="p-6">
                {/* Close */}
                <div className="flex justify-end mb-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirmAction(null)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Content based on action type */}
                {confirmAction.type === 'downgrade' ? (
                  <>
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                        <Calendar className="w-8 h-8 text-amber-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Schedule Downgrade</h3>
                      <p className="text-slate-400 mt-2">
                        Your plan will change to <span className="text-white font-medium capitalize">{confirmAction.tier}</span> at the end of your current billing period.
                      </p>
                    </div>
                    
                    <div className="p-4 bg-slate-800/50 rounded-lg mb-6 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">New Plan</span>
                        <span className="text-white capitalize">{confirmAction.tier} ({confirmAction.cycle})</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Effective Date</span>
                        <span className="text-white">{new Date(subscription?.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Payment Now</span>
                        <span className="text-green-400">None</span>
                      </div>
                    </div>

                    <Button
                      onClick={handleConfirmAction}
                      disabled={processing}
                      className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                    >
                      {processing ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Confirm Downgrade'}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="text-center mb-6">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                        confirmAction.type === 'upgrade' ? 'bg-green-500/20' : 'bg-teal-500/20'
                      }`}>
                        {confirmAction.type === 'upgrade' ? (
                          <Zap className="w-8 h-8 text-green-400" />
                        ) : (
                          <CreditCard className="w-8 h-8 text-teal-400" />
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-white capitalize">
                        {confirmAction.type === 'upgrade' ? 'Upgrade to' : confirmAction.type === 'renew' ? 'Renew' : 'Subscribe to'} {confirmAction.tier}
                      </h3>
                      <p className="text-slate-400 mt-2">
                        {confirmAction.type === 'upgrade' 
                          ? 'Your new plan starts immediately.' 
                          : confirmAction.type === 'renew'
                          ? 'Days will be added to your current subscription.'
                          : 'Start your subscription today.'}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-slate-800/50 rounded-lg mb-6 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Plan</span>
                        <span className="text-white capitalize">{confirmAction.tier} ({confirmAction.cycle})</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Amount</span>
                        <span className="text-2xl font-bold text-teal-400">{formatCurrency(confirmAction.price)}</span>
                      </div>
                      {confirmAction.type === 'upgrade' && (
                        <div className="pt-2 border-t border-slate-700 text-xs text-slate-400">
                          Billing cycle resets from today ({confirmAction.cycle === 'yearly' ? '365' : '30'} days)
                        </div>
                      )}
                      {confirmAction.type === 'renew' && subscription?.days_remaining > 0 && (
                        <div className="pt-2 border-t border-slate-700 text-xs text-green-400">
                          +{confirmAction.cycle === 'yearly' ? '365' : '30'} days added to your {subscription.days_remaining} remaining
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={handleConfirmAction}
                      disabled={processing}
                      className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 h-12"
                    >
                      {processing ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Pay {formatCurrency(confirmAction.price)}
                        </>
                      )}
                    </Button>
                  </>
                )}

                <p className="text-center text-xs text-slate-500 mt-4">
                  {confirmAction.type === 'downgrade' 
                    ? 'You can cancel this anytime before the effective date.'
                    : 'Secure payment powered by Razorpay'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
